import type { ProductPaymentMethod, ProductPaymentSettings } from '@/services';
import { toaster } from '@/shared/ui/toaster.component';
import { FINANCE_METHOD_LABELS } from '@/shared/utils/finance-method-label.util';
import { useState } from 'react';
import {
  BILLING_METHOD_LABELS,
  billingMethodLabel,
} from '../utils/payment-settings-labels.util';
import { isShownInProposalCombo } from '../utils/proposal-combo.util';
import {
  diffPaymentMethodRow,
  type SettingsChange,
} from '../utils/settings-diff.util';
import { useSavePaymentMethod } from './use-save-payment-method.hook';

export type CatalogRowView = {
  method: ProductPaymentMethod;
  billingLabel: string;
  /** Forma do gateway da linha, quando há ("PIX", "Todos os meios"…). */
  gatewayLabel?: string;
  bankLabel: string;
  isActive: boolean;
  isShownInCombo: boolean;
};

export type MethodEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; method: ProductPaymentMethod }
  | null;

type PendingStatusChange = {
  method: ProductPaymentMethod;
  changes: SettingsChange[];
};

/**
 * Aba "Catálogo de formas": linhas prontas para a tabela, abertura do
 * formulário (adicionar/editar) e a troca de status com confirmação.
 */
export function usePaymentMethodsCatalog(
  settings: ProductPaymentSettings,
  canEdit: boolean
) {
  const mutation = useSavePaymentMethod(settings.productCode);
  const [editor, setEditor] = useState<MethodEditorState>(null);
  const [pendingStatus, setPendingStatus] =
    useState<PendingStatusChange | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const isEditable = canEdit && settings.catalogEditable;

  const rows: CatalogRowView[] = settings.paymentMethods.map(method => ({
    method,
    billingLabel: billingMethodLabel(method.billingMethod),
    gatewayLabel:
      method.gatewayMethod === 'ALL'
        ? BILLING_METHOD_LABELS.TODOS_MEIOS
        : method.gatewayMethod
          ? FINANCE_METHOD_LABELS[method.gatewayMethod]
          : undefined,
    bankLabel:
      method.bankNumber !== undefined ? String(method.bankNumber) : '—',
    isActive: method.status === 'ACTIVE',
    isShownInCombo: isShownInProposalCombo(method, settings.gateway),
  }));

  const requestStatusToggle = (method: ProductPaymentMethod) => {
    const next = {
      ...method,
      status:
        method.status === 'ACTIVE'
          ? ('INACTIVE' as const)
          : ('ACTIVE' as const),
    };
    setStatusError(null);
    setPendingStatus({ method, changes: diffPaymentMethodRow(method, next) });
  };

  const closeStatus = () => {
    if (mutation.isPending) return;
    setPendingStatus(null);
    setStatusError(null);
  };

  const confirmStatus = async () => {
    if (!pendingStatus) return;
    const { method } = pendingStatus;
    const nextStatus = method.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await mutation.mutateAsync({
        kind: 'update',
        code: method.code,
        payload: {
          description: method.description,
          billingMethod: method.billingMethod,
          bankNumber: method.bankNumber,
          status: nextStatus,
          version: method.version ?? 0,
        },
      });
      toaster.create({
        type: 'success',
        title: nextStatus === 'ACTIVE' ? 'Forma reativada' : 'Forma desativada',
        description: `${method.code} · ${method.description}`,
      });
      setPendingStatus(null);
    } catch (error) {
      setStatusError(
        error instanceof Error
          ? error.message
          : 'Não foi possível alterar a forma de pagamento.'
      );
    }
  };

  return {
    rows,
    isEditable,
    catalogEditable: settings.catalogEditable,
    editor,
    openCreate: () => setEditor({ mode: 'create' }),
    openEdit: (method: ProductPaymentMethod) =>
      setEditor({ mode: 'edit', method }),
    closeEditor: () => setEditor(null),
    pendingStatus,
    statusError,
    isSavingStatus: mutation.isPending,
    requestStatusToggle,
    closeStatus,
    confirmStatus,
  };
}
